// Wave 3 — FileCard decomposition (pure, side-effect-free document helpers).
// Extracted verbatim from pages/FileCard.js so the decomposition is
// behavior-preserving: every function below reproduces the exact inline logic
// it replaces. No I/O, no DOM access — safe to unit test.

export const normalizeRouteValue = (value) =>
  String(value || "").trim().toUpperCase();

export const formatTimestamp = (date) => {
  if (!date) return "N/A";
  try {
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return "N/A";
    return parsed.toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "N/A";
  }
};

export const uniquePath = (values) =>
  values.filter(Boolean).filter((value, index, all) =>
    all.findIndex(
      (candidate) => normalizeRouteValue(candidate) === normalizeRouteValue(value)
    ) === index
  );

export const getFileExtension = (fileName) =>
  String(fileName || "").split(".").pop().toLowerCase();

export const isClassified = (classification) =>
  ["confidential", "secret"].includes(String(classification || "").toLowerCase());

export const isStampableFileType = (ext) =>
  ext === "pdf" || ["jpg", "jpeg", "png"].includes(ext);

export const filterOptions = (options, query) => {
  const normalizedQuery = String(query || "").trim().toLowerCase();
  if (!normalizedQuery) return options;
  return options.filter((option) =>
    String(option || "").toLowerCase().includes(normalizedQuery)
  );
};

export const findExactOption = (options, value) => {
  const normalizedValue = String(value || "").trim().toLowerCase();
  if (!normalizedValue) return "";
  return (
    options.find(
      (option) => String(option || "").trim().toLowerCase() === normalizedValue
    ) || ""
  );
};

// F1: forwarding options are derived from the DB structure snapshot; this pure
// walker flattens any snapshot tree into recipient path entries.
export const buildRecipientEntries = (tree) => {
  const entries = [];

  Object.entries(tree || {}).forEach(([formation, formationNode]) => {
    const subBranches = formationNode?.subBranches || {};

    Object.entries(subBranches).forEach(([branchName, branchNode]) => {
      if (Array.isArray(branchNode)) {
        branchNode.forEach((recipient) => {
          entries.push({
            formation,
            branch: branchName,
            subBranch: "",
            section: "",
            recipient,
          });
        });
        return;
      }

      if (branchNode && typeof branchNode === "object") {
        Object.entries(branchNode).forEach(([subBranchName, subBranchNode]) => {
          if (Array.isArray(subBranchNode)) {
            subBranchNode.forEach((recipient) => {
              entries.push({
                formation,
                branch: branchName,
                subBranch: subBranchName,
                section: "",
                recipient,
              });
            });
          } else if (subBranchNode && typeof subBranchNode === "object") {
            Object.entries(subBranchNode).forEach(([sectionName, sectionNode]) => {
              if (Array.isArray(sectionNode)) {
                sectionNode.forEach((recipient) => {
                  entries.push({
                    formation,
                    branch: branchName,
                    subBranch: subBranchName,
                    section: sectionName,
                    recipient,
                  });
                });
              }
            });
          }
        });
      }
    });
  });

  return entries;
};

export const flattenComments = (arr) =>
  (arr || [])
    .map((c) => `${c.role || "User"}: ${c.text}`)
    .filter(Boolean);

// Combined history + reviewHistory trail (newest first).
export const buildCombinedHistory = (file = {}) => {
  const reviewTrail = (file.reviewHistory || []).map((h) => ({
    action: h.action,
    by: h.reviewedByRole || h.reviewedBy || h.by,
    at: h.reviewedAt,
    comments: flattenComments(h.comments),
    source: "reviewHistory",
  }));

  const historyTrail = (file.history || []).map((h) => ({
    action: h.action,
    by: h.reviewedByRole || h.reviewedBy || h.by,
    at: h.timestamp,
    comments: flattenComments(h.comments),
    returnToRole: h.returnToRole,
    attachments: Array.isArray(h.attachments) ? h.attachments : [],
    source: "history",
  }));

  return [...reviewTrail, ...historyTrail].sort(
    (a, b) => new Date(b.at || 0) - new Date(a.at || 0)
  );
};

// Delivery & receipt state machine (Sent → Delivered → Opened → Actioned).
export const deriveReceipt = (file = {}) => {
  const normalizedStatus = String(file.status || "").toLowerCase();
  const latestRoute = [...(file.routeEvents || [])].sort(
    (a, b) => new Date(b.deliveredAt || 0) - new Date(a.deliveredAt || 0)
  )[0];
  const latestDeliveryKey = latestRoute?.deliveredAt
    ? new Date(latestRoute.deliveredAt).toISOString()
    : "";
  const currentReceipts = [...(file.readReceipts || [])].filter((receipt) =>
    latestDeliveryKey
      ? receipt?.deliveryKey === latestDeliveryKey
      : String(receipt?.role || "").trim().toUpperCase() ===
      String(file?.currentLevel || "").trim().toUpperCase()
  );
  const latestReceipt = currentReceipts
    .sort((a, b) => new Date(b.firstOpenedAt || 0) - new Date(a.firstOpenedAt || 0))[0];
  const actionedStatuses = new Set(["actioned", "reviewed", "archived"]);
  const state = actionedStatuses.has(normalizedStatus)
    ? "Actioned"
    : latestReceipt
      ? "Opened"
      : latestRoute
        ? "Delivered"
        : "Sent";
  const at =
    state === "Opened"
      ? latestReceipt?.firstOpenedAt
      : state === "Delivered"
        ? latestRoute?.deliveredAt
        : state === "Actioned"
          ? file?.updatedAt
          : file?.createdAt;
  const role =
    latestReceipt?.role || latestRoute?.role || file?.currentLevel || "Recipient";

  return { state, at, role, userName: latestReceipt?.userName || "" };
};

// Workflow stepper model for the document timeline.
export const WORKFLOW_STEPS = ["Sent", "Delivered", "Opened", "Actioned"];

export const deriveWorkflowSteps = (receiptState, isArchived = false) => {
  const steps = WORKFLOW_STEPS.map((label) => ({
    label,
    state:
      label === receiptState
        ? "current"
        : WORKFLOW_STEPS.indexOf(label) < WORKFLOW_STEPS.indexOf(receiptState)
          ? "done"
          : "pending",
  }));
  if (isArchived) {
    return [
      ...steps.map((step) => (step.state === "current" ? { ...step, state: "done" } : step)),
      { label: "Archived", state: "current" },
    ];
  }
  return steps;
};

export const getStatusTone = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized.includes("approv") || normalized.includes("complete")) return "success";
  if (
    normalized.includes("review") ||
    normalized.includes("resubmission") ||
    normalized.includes("return")
  )
    return "warning";
  if (normalized.includes("reject") || normalized.includes("delete")) return "danger";
  if (normalized.includes("archiv")) return "neutral";
  return "info";
};

// Origin/destination path derivation (uploader vs recipient perspective).
export const derivePerspective = (file = {}, viewer = {}) => {
  const originScope = file?.originScope || {};
  const latestRoute = [...(file.routeEvents || [])].sort(
    (a, b) => new Date(b.deliveredAt || 0) - new Date(a.deliveredAt || 0)
  )[0];
  const receipt = deriveReceipt(file);

  const viewerIdentityKeys = [viewer?.userId, viewer?.username, viewer?.serviceNumber]
    .map(normalizeRouteValue)
    .filter(Boolean);
  const uploaderIdentityKeys = [file?.uploadedByUserId, file?.uploadedBy]
    .map(normalizeRouteValue)
    .filter(Boolean);
  const isUploaderView = uploaderIdentityKeys.some((identity) =>
    viewerIdentityKeys.includes(identity)
  );

  const originPath = uniquePath([
    originScope.unitBranch,
    originScope.subBranch,
    originScope.branch,
    originScope.unit,
  ]);
  const destinationPath = uniquePath([
    latestRoute?.subBranch || file?.subBranch,
    latestRoute?.branch || file?.branch,
    latestRoute?.unitBranch || file?.unitBranch,
    latestRoute?.unit || file?.unit,
  ]);

  const perspectiveLabel = isUploaderView ? "Sent to" : "From / origin";
  const perspectivePath = isUploaderView
    ? uniquePath([receipt.role, ...destinationPath])
    : uniquePath([file?.uploadedByRole || file?.uploadedBy || "Unknown sender", ...originPath]);
  const currentRecipientPath = uniquePath([file?.currentLevel || receipt.role, ...destinationPath]);

  return { isUploaderView, perspectiveLabel, perspectivePath, currentRecipientPath };
};

// Rank inference for appointment stamp templates (role-string based).
export const inferStampRankFromRole = (roleString) => {
  const joined = String(roleString || "").toUpperCase().replace(/\s+/g, " ").trim();
  if (!joined) return "MAJ";

  if (
    joined.includes("LIEUTENANT COLONEL") ||
    joined.includes("LT COL") ||
    joined.includes("LTCOL")
  ) {
    return "LT COL";
  }
  if (joined.includes("COLONEL") || joined.includes(" COL ") || joined.startsWith("COL ")) {
    return "COL";
  }
  if (joined.includes("MAJOR") || joined.includes(" MAJ ") || joined.startsWith("MAJ ")) {
    return "MAJ";
  }

  if (joined.includes("SO1") || joined.includes("CHIEF OF BRANCH")) return "LT COL";
  if (joined.includes("SO2")) return "MAJ";

  return "MAJ";
};
